import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth, API } from "@/App";
import { Map, Compass, User, MapPin, Navigation, Beer, Star, ChevronRight, Loader2, RefreshCw } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const CATEGORIES = [
  { value: 'all', label: 'All Stops', emoji: '📍' },
  { value: 'dive_bar', label: 'Dive Bars', emoji: '🍺' },
  { value: 'restaurant', label: 'Restaurants', emoji: '🍔' },
  { value: 'landmark', label: 'Landmarks', emoji: '🗿' },
  { value: 'hotel', label: 'Hotels', emoji: '🏨' },
  { value: 'gas_station', label: 'Gas Stations', emoji: '⛽' },
];

const loadGoogleMapsScript = () => {
  return new Promise((resolve, reject) => {
    if (window.google && window.google.maps) {
      resolve(window.google);
      return;
    }

    const existingScript = document.getElementById("google-maps-script");
    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(window.google));
      return;
    }

    const script = document.createElement("script");
    script.id = "google-maps-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

const Explore = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const userMarkerRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [mapLoading, setMapLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [nearbyStops, setNearbyStops] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedStop, setSelectedStop] = useState(null);
  const [radius, setRadius] = useState(25);

  // Get user location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.error("Geolocation error:", error);
          // Default to San Francisco
          setUserLocation({ lat: 37.7749, lng: -122.4194 });
          toast.error("Location access denied. Showing San Francisco area.");
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      setUserLocation({ lat: 37.7749, lng: -122.4194 });
    }
  }, []);

  // Initialize map
  useEffect(() => {
    if (!userLocation) return;

    const initMap = async () => {
      try {
        await loadGoogleMapsScript();
        
        if (!mapRef.current || mapInstanceRef.current) return;

        const mapStyles = [
          { elementType: "geometry", stylers: [{ color: "#1d1d1d" }] },
          { elementType: "labels.text.stroke", stylers: [{ color: "#1d1d1d" }] },
          { elementType: "labels.text.fill", stylers: [{ color: "#8a8a8a" }] },
          { featureType: "road", elementType: "geometry", stylers: [{ color: "#2c2c2c" }] },
          { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#8a8a8a" }] },
          { featureType: "water", elementType: "geometry", stylers: [{ color: "#0e0e0e" }] },
          { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
        ];

        mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
          center: userLocation,
          zoom: 11,
          styles: mapStyles,
          disableDefaultUI: true,
          zoomControl: true,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });

        // Add user location marker
        userMarkerRef.current = new window.google.maps.Marker({
          position: userLocation,
          map: mapInstanceRef.current,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 10,
            fillColor: "#FF4500",
            fillOpacity: 1,
            strokeColor: "#ffffff",
            strokeWeight: 3,
          },
          title: "Your Location",
        });

        setMapLoading(false);
      } catch (error) {
        console.error("Failed to init map:", error);
        setMapLoading(false);
      }
    };

    initMap();
  }, [userLocation]);

  // Fetch nearby stops
  const fetchNearbyStops = useCallback(async () => {
    if (!userLocation) return;

    setLoading(true);
    try {
      const categoryParam = selectedCategory === "all" ? "" : `&category=${selectedCategory}`;
      const response = await axios.get(
        `${API}/explore/nearby?lat=${userLocation.lat}&lng=${userLocation.lng}&radius=${radius}${categoryParam}`
      );
      setNearbyStops(response.data.stops || []);
    } catch (error) {
      console.error("Failed to fetch nearby stops:", error);
      toast.error("Failed to load nearby stops");
    } finally {
      setLoading(false);
    }
  }, [userLocation, selectedCategory, radius]);

  useEffect(() => {
    fetchNearbyStops();
  }, [fetchNearbyStops]);

  // Update markers on map
  useEffect(() => {
    if (!mapInstanceRef.current || !window.google) return;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    // Add markers for stops
    nearbyStops.forEach(stop => {
      const coords = stop.coordinates;
      if (!coords?.lat || !coords?.lng) return;

      const markerColor = {
        dive_bar: "#FF4500",
        restaurant: "#FFD700",
        landmark: "#00FFFF",
        hotel: "#9333EA",
        gas_station: "#22C55E",
      }[stop.category] || "#FF4500";

      const marker = new window.google.maps.Marker({
        position: { lat: coords.lat, lng: coords.lng },
        map: mapInstanceRef.current,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: markerColor,
          fillOpacity: 0.9,
          strokeColor: "#ffffff",
          strokeWeight: 2,
        },
        title: stop.name,
      });

      marker.addListener("click", () => {
        setSelectedStop(stop);
      });

      markersRef.current.push(marker);
    });
  }, [nearbyStops]);

  const getCategoryEmoji = (category) => {
    return CATEGORIES.find(c => c.value === category)?.emoji || '📍';
  };

  const centerOnUser = () => {
    if (mapInstanceRef.current && userLocation) {
      mapInstanceRef.current.panTo(userLocation);
      mapInstanceRef.current.setZoom(12);
    }
  };

  return (
    <div className="mobile-container asphalt-texture min-h-screen pb-24">
      {/* Map Container */}
      <div className="relative h-[45vh]">
        <div ref={mapRef} className="w-full h-full" />
        
        {mapLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-[#050505]">
            <Loader2 className="w-8 h-8 text-bronze animate-spin" />
          </div>
        )}

        {/* Map Controls */}
        <div className="absolute top-4 left-4 right-4 flex justify-between items-start">
          <div className="glass rounded-lg px-3 py-2">
            <h1 className="font-oswald text-sm font-bold uppercase flex items-center gap-2">
              <Compass className="w-4 h-4 text-cyan-400" />
              Explore
            </h1>
          </div>
          
          <Button
            size="icon"
            className="glass rounded-full w-10 h-10"
            onClick={centerOnUser}
          >
            <Navigation className="w-4 h-4" />
          </Button>
        </div>

        {/* Selected Stop Preview */}
        {selectedStop && (
          <div 
            className="absolute bottom-4 left-4 right-4 glass rounded-xl p-4 animate-slide-up cursor-pointer"
            onClick={() => navigate(`/trip/${selectedStop.trip_id}`)}
          >
            <div className="flex items-start gap-3">
              <div className="text-2xl">{getCategoryEmoji(selectedStop.category)}</div>
              <div className="flex-1">
                <h3 className="font-oswald font-semibold">{selectedStop.name}</h3>
                <p className="text-xs text-gray-400 mt-1">{selectedStop.address?.slice(0, 40)}...</p>
                <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                  <span>{selectedStop.distance} mi away</span>
                  {selectedStop.avg_rating && (
                    <span className="flex items-center gap-1">
                      <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                      {selectedStop.avg_rating}
                    </span>
                  )}
                  <span>in "{selectedStop.trip_title}"</span>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="p-4">
        <div className="flex gap-2 mb-4">
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="flex-1 h-10 bg-[#121212] border-white/10">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10">
              {CATEGORIES.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>
                  {cat.emoji} {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={radius.toString()} onValueChange={(v) => setRadius(parseInt(v))}>
            <SelectTrigger className="w-28 h-10 bg-[#121212] border-white/10">
              <SelectValue placeholder="Radius" />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10">
              <SelectItem value="5">5 miles</SelectItem>
              <SelectItem value="10">10 miles</SelectItem>
              <SelectItem value="25">25 miles</SelectItem>
              <SelectItem value="50">50 miles</SelectItem>
              <SelectItem value="100">100 miles</SelectItem>
            </SelectContent>
          </Select>

          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10"
            onClick={fetchNearbyStops}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Nearby Stops List */}
        <div className="flex justify-between items-center mb-3">
          <h2 className="font-oswald uppercase text-sm tracking-wide">
            Nearby {selectedCategory === "all" ? "Stops" : CATEGORIES.find(c => c.value === selectedCategory)?.label}
          </h2>
          <span className="text-xs text-gray-500">{nearbyStops.length} found</span>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="glass rounded-xl p-4 animate-pulse">
                <div className="h-5 bg-white/10 rounded w-1/2 mb-2" />
                <div className="h-3 bg-white/10 rounded w-3/4" />
              </div>
            ))}
          </div>
        ) : nearbyStops.length === 0 ? (
          <div className="glass rounded-xl p-6 text-center">
            <MapPin className="w-10 h-10 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">No stops found nearby</p>
            <p className="text-gray-600 text-xs mt-1">Try increasing the search radius</p>
          </div>
        ) : (
          <div className="space-y-2">
            {nearbyStops.slice(0, 10).map((stop) => (
              <div
                key={stop.stop_id}
                data-testid={`explore-stop-${stop.stop_id}`}
                className={`glass rounded-xl p-4 cursor-pointer transition-all ${
                  selectedStop?.stop_id === stop.stop_id ? 'border-bronze' : 'hover:border-bronze/50'
                }`}
                onClick={() => {
                  setSelectedStop(stop);
                  if (mapInstanceRef.current && stop.coordinates) {
                    mapInstanceRef.current.panTo(stop.coordinates);
                    mapInstanceRef.current.setZoom(14);
                  }
                }}
              >
                <div className="flex items-start gap-3">
                  <div className="text-xl">{getCategoryEmoji(stop.category)}</div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-sm">{stop.name}</h3>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {stop.distance} mi
                      </span>
                      {stop.avg_rating && (
                        <span className="flex items-center gap-1">
                          <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                          {stop.avg_rating} ({stop.review_count})
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                      From route: {stop.trip_title}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <div className="bottom-nav glass safe-bottom">
        <div className="flex justify-around py-3">
          <button
            data-testid="nav-dashboard"
            className="flex flex-col items-center gap-1 text-gray-500 hover:text-white transition-colors"
            onClick={() => user ? navigate('/dashboard') : navigate('/login')}
          >
            <Map className="w-5 h-5" />
            <span className="text-[10px] uppercase tracking-wide">My Trips</span>
          </button>
          <button
            data-testid="nav-explore"
            className="flex flex-col items-center gap-1 text-cyan-400"
            onClick={() => navigate('/explore')}
          >
            <Compass className="w-5 h-5" />
            <span className="text-[10px] uppercase tracking-wide">Explore</span>
          </button>
          <button
            data-testid="nav-discover"
            className="flex flex-col items-center gap-1 text-gray-500 hover:text-white transition-colors"
            onClick={() => navigate('/discover')}
          >
            <Beer className="w-5 h-5" />
            <span className="text-[10px] uppercase tracking-wide">Discover</span>
          </button>
          <button
            data-testid="nav-profile"
            className="flex flex-col items-center gap-1 text-gray-500 hover:text-white transition-colors"
            onClick={() => user ? navigate('/profile') : navigate('/login')}
          >
            <User className="w-5 h-5" />
            <span className="text-[10px] uppercase tracking-wide">Profile</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Explore;
